/**
 * SES per-link click tracking (`ses:tags="b:<id>"` / `ses:no-track`) — shared
 * by the block defaults, the designer store (derive-on-create) and the HTML
 * serializer (emit-on-export).
 *
 * HARD CONSTRAINT, verified empirically against our own SES configuration
 * set: SES accepts exactly ONE key:value pair in `ses:tags`. A single pair
 * (`ses:tags="b:hero_cta"`) returns `linkTags` correctly on the SNS click
 * event; three comma-separated pairs returned `linkTags: null` — the whole
 * attribute was discarded. So this module never builds more than one pair,
 * always under the key `b`, and never lets a comma reach the attribute value.
 * Anything else worth tracking (position, region, block type) gets packed
 * into the value itself, underscore-delimited.
 */

export const MAX_TAG_LEN = 200

/**
 * Sanitizes on input as the user types — never rejects. ASCII letters,
 * digits, underscores and dashes only; capped at 200 chars (SES's own limit
 * is 256; the extra headroom is for any future prefixing).
 */
export const sanitizeTag = (s) =>
  String(s == null ? '' : s)
    .trim().toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/(?:^_)|(?:_$)/g, '')
    .slice(0, MAX_TAG_LEN)

// Stable 4-char suffix — same idiom as emailDesignerStore's genId()/genColId,
// just shorter (it rides inside a 200-char tag value, not a React key). This
// is what makes three "Shop Now" buttons distinguishable from one another.
export const genTrackingSuffix = () => Math.random().toString(36).slice(2, 6)

/** "Click Here" -> "click_here_a7f3". Empty/unsanitizable label -> "link_xxxx". */
export function deriveTrackingId(label) {
  const base = sanitizeTag(label) || 'link'
  return `${base}_${genTrackingSuffix()}`.slice(0, MAX_TAG_LEN)
}

/**
 * Appends a disambiguating suffix until `id` is absent from `existingIds`.
 * `hint` (e.g. "header"/"footer") is tried first since it reads better than
 * a bare counter; falls back to _2, _3, … if the hint itself collides too.
 */
export function ensureUniqueTrackingId(id, existingIds, hint) {
  if (!id || !existingIds || !existingIds.has(id)) return id
  if (hint) {
    const withHint = sanitizeTag(`${id}_${hint}`)
    if (withHint && !existingIds.has(withHint)) return withHint
  }
  let n = 2
  let candidate = `${id}_${n}`
  while (existingIds.has(candidate)) { n += 1; candidate = `${id}_${n}` }
  return candidate.slice(0, MAX_TAG_LEN)
}

// Every tracking-id-bearing field, across every block type that has a single
// (non-repeating, non-per-platform) anchor. The single source of truth for
// both collision-checking at creation time and the "duplicate ID" lint rule.
const SINGLE_TRACKING_FIELDS = [
  'trackingId', 'thumbnailTrackingId', 'logoTrackingId', 'unsubscribeTrackingId',
]
const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube']

function collectFromProps(props, out) {
  if (!props) return
  SINGLE_TRACKING_FIELDS.forEach(f => { if (props[f]) out.push(props[f]) })
  if (Array.isArray(props.links)) {
    props.links.forEach(l => { if (l?.trackingId) out.push(l.trackingId) })
  }
  SOCIAL_PLATFORMS.forEach(k => { if (props[`${k}TrackingId`]) out.push(props[`${k}TrackingId`]) })
}

/** Walks the whole block tree (incl. column children) collecting every tracking id currently persisted anywhere in the template. */
export function collectTrackingIds(blocks) {
  const out = []
  const walk = (list) => {
    (list || []).forEach(b => {
      collectFromProps(b.props, out)
      if (b.type === 'columns' && Array.isArray(b.props?.columnDefs)) {
        b.props.columnDefs.forEach(col => walk(col.blocks || []))
      }
    })
  }
  walk(blocks)
  return out
}

/**
 * Builds the literal HTML fragment to splice into an `<a ...>` tag: a leading
 * space plus either `ses:no-track` or `ses:tags="b:<id>"`, or '' when
 * neither applies. Never emits `ses:tags="b:"` — an empty value is worse
 * than no attribute at all.
 */
export function sesAttr({ trackingId, noTrack } = {}) {
  if (noTrack) return ' ses:no-track'
  const id = sanitizeTag(trackingId)
  return id ? ` ses:tags="b:${id}"` : ''
}

// Block types whose tracking id(s) can be safely derived straight from the
// DEFAULT props (their trigger field — label/url — is always non-empty on a
// freshly created block). Others (Image.link, Countdown.link, the header
// bar's logoLink) start empty and are handled by deriveOnFieldChange below,
// the moment the field is actually filled in.
export function withDerivedTracking(blockType, props, existingBlocks) {
  const existing = new Set(collectTrackingIds(existingBlocks || []))
  const claim = (label) => {
    const id = ensureUniqueTrackingId(deriveTrackingId(label), existing)
    existing.add(id)
    return id
  }

  const next = { ...props }

  if (blockType === 'button' && !next.trackingId) {
    next.trackingId = claim(next.label)
  }
  if (blockType === 'headerBar' && Array.isArray(next.links)) {
    next.links = next.links.map(l => (!l.trackingId
      ? { ...l, trackingId: claim(l.label || l.alt || (l.kind === 'image' ? 'image_link' : 'link')) }
      : l))
  }
  if (blockType === 'story') {
    if (!next.trackingId) next.trackingId = claim(next.ctaLabel || 'story_cta')
    if (!next.thumbnailTrackingId) next.thumbnailTrackingId = claim(next.storyTitle || 'story_thumbnail')
  }
  if (blockType === 'video') {
    if (!next.thumbnailTrackingId) next.thumbnailTrackingId = claim('video_thumbnail')
    if (!next.trackingId) next.trackingId = claim('watch_video')
  }
  // Image/Countdown/header-logo links are USUALLY empty on a fresh block
  // (deriveOnFieldChange below is their normal path, the moment the link
  // field is first filled in) — but a duplicated block can already have one
  // set (then cleared by clearTrackingIds), so cover that case here too.
  if (blockType === 'image' && next.link && !next.trackingId) {
    next.trackingId = claim(next.alt || 'image_link')
  }
  if (blockType === 'countdown' && next.link && !next.trackingId) {
    next.trackingId = claim('countdown')
  }
  if (blockType === 'headerBar' && next.logoLink && !next.logoTrackingId) {
    next.logoTrackingId = claim('logo')
  }
  if (blockType === 'footer' && next.unsubscribeUrl && !next.unsubscribeTrackingId && !next.unsubscribeNoTrack) {
    next.unsubscribeTrackingId = claim(next.unsubscribeLabel || 'unsubscribe')
  }

  return next
}

// trigger field -> tracking-id field, for links that usually start EMPTY on a
// fresh block and only get filled in later via the Properties panel — unlike
// Button/Video, whose trigger field is non-empty from the default props.
const UPDATE_TRIGGERS = {
  image: [['link', 'trackingId']],
  countdown: [['link', 'trackingId']],
  headerBar: [['logoLink', 'logoTrackingId']],
  footer: [['unsubscribeUrl', 'unsubscribeTrackingId']],
}

/**
 * Called from the store's updateBlock, AFTER merging the incoming update onto
 * the block's existing props. If a trigger field just became non-empty and
 * its tracking-id field is still empty, derives+freezes one right then — the
 * only chance these particular fields get, since they don't exist yet at
 * block-creation time. Already-set tracking ids are never touched (frozen).
 */
export function deriveOnFieldChange(blockType, props, existingBlocks) {
  const triggers = UPDATE_TRIGGERS[blockType]
  if (!triggers) return props
  let next = props
  let existing = null
  triggers.forEach(([triggerField, idField]) => {
    if (next[triggerField] && !next[idField] && !next[idField.replace('TrackingId', 'NoTrack')]) {
      if (!existing) existing = new Set(collectTrackingIds(existingBlocks || []))
      const label = triggerField === 'logoLink' ? 'logo'
        : triggerField === 'unsubscribeUrl' ? (next.unsubscribeLabel || 'unsubscribe')
        : (next.label || next.alt || triggerField)
      const id = ensureUniqueTrackingId(deriveTrackingId(label), existing)
      existing.add(id)
      next = { ...next, [idField]: id }
    }
  })
  return next
}

/** Clears every tracking id on a block's props — used when duplicating a block, so the copy gets its own fresh id(s) rather than sharing (and merging click counts with) the original. */
export function clearTrackingIds(props) {
  if (!props) return props
  const next = { ...props }
  SINGLE_TRACKING_FIELDS.forEach(f => { if (next[f] !== undefined) next[f] = '' })
  if (Array.isArray(next.links)) next.links = next.links.map(l => ({ ...l, trackingId: '' }))
  return next
}

// ── Social Icons: computed at export time, not stored ────────────────────
//
// Unlike every other block type, a Social block's tracking id is a pure
// function of (platform, whether that platform repeats elsewhere in the
// template) — there's nothing to "rename" the way a button label changes, so
// there's no freezing concern and no need to persist anything for the common
// case. Only the rare override (Advanced disclosure in the panel) is stored.

/** Nearest headerBar/footer block to `topIndex` in the top-level `blocks` array — used to turn a repeated platform into `social_instagram_header` / `_footer` instead of a bare counter. */
function nearestRegion(blocks, topIndex) {
  let before = -1, after = -1
  for (let i = topIndex - 1; i >= 0; i--) {
    if (blocks[i].type === 'headerBar' || blocks[i].type === 'footer') { before = i; break }
  }
  for (let i = topIndex + 1; i < blocks.length; i++) {
    if (blocks[i].type === 'headerBar' || blocks[i].type === 'footer') { after = i; break }
  }
  if (before === -1 && after === -1) return null
  const chosen = before === -1 ? after : after === -1 ? before
    : (topIndex - before <= after - topIndex ? before : after)
  return blocks[chosen].type === 'footer' ? 'footer' : 'header'
}

/**
 * Builds `Map<socialBlockId, { [platform]: resolvedTrackingId }>` for every
 * Social Icons block in the template (top-level and nested in a column).
 * Called once per export by blocksToHtml and threaded down through render.
 */
export function buildSocialTrackingPlan(blocks) {
  const flat = []
  ;(blocks || []).forEach((b, i) => {
    flat.push({ block: b, topIndex: i })
    if (b.type === 'columns' && Array.isArray(b.props?.columnDefs)) {
      b.props.columnDefs.forEach(col => (col.blocks || []).forEach(sb => flat.push({ block: sb, topIndex: i })))
    }
  })
  const socialEntries = flat.filter(e => e.block.type === 'social')

  const platformCount = {}
  socialEntries.forEach(({ block }) => {
    SOCIAL_PLATFORMS.forEach(key => {
      if (block.props[`${key}Enabled`] !== false && block.props[key]) {
        platformCount[key] = (platformCount[key] || 0) + 1
      }
    })
  })

  const used = new Set()
  const plan = new Map()
  socialEntries.forEach(({ block, topIndex }, idx) => {
    const perPlatform = {}
    SOCIAL_PLATFORMS.forEach(key => {
      if (!(block.props[`${key}Enabled`] !== false && block.props[key])) return
      const override = sanitizeTag(block.props[`${key}TrackingId`] || '')
      let id = override
      if (!id) {
        id = `social_${key}`
        if (platformCount[key] > 1) {
          const region = nearestRegion(blocks || [], topIndex)
          id = region ? `social_${key}_${region}` : `social_${key}_${idx + 1}`
        }
      }
      let final = id
      let n = 2
      while (used.has(final)) { final = `${id}_${n++}` }
      used.add(final)
      perPlatform[key] = final
    })
    plan.set(block.id, perPlatform)
  })
  return plan
}
